import { motion } from 'framer-motion';
import { User, Briefcase, Building2, FileText, Scale, ClipboardList, BookOpen } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { SEO } from '../components/SEO';

const teamTranslations = {
  it: {
    notaiTitle: 'I Notai',
    collaboratoriTitle: 'I Collaboratori',
    collaboratoriSubtitle: 'Il nostro team di professionisti',
    areas: {
      immobiliare: 'Immobiliare',
      'diritto societario': 'Diritto Societario',
      'trascrizioni e iscrizioni': 'Trascrizioni e Iscrizioni',
      successioni: 'Successioni',
      'registrazioni e adempimenti': 'Registrazioni e Adempimenti',
      amministrazione: 'Amministrazione',
      'diritto immobiliare': 'Diritto Immobiliare'
    }
  },
  en: {
    notaiTitle: 'The Notaries',
    collaboratoriTitle: 'Our Team',
    collaboratoriSubtitle: 'Our team of professionals',
    areas: {
      immobiliare: 'Real Estate',
      'diritto societario': 'Corporate Law',
      'trascrizioni e iscrizioni': 'Transcriptions & Registrations',
      successioni: 'Inheritance',
      'registrazioni e adempimenti': 'Registrations & Compliance',
      amministrazione: 'Administration',
      'diritto immobiliare': 'Real Estate Law'
    }
  },
  de: {
    notaiTitle: 'Die Notare',
    collaboratoriTitle: 'Unser Team',
    collaboratoriSubtitle: 'Unser Team von Fachleuten',
    areas: {
      immobiliare: 'Immobilien',
      'diritto societario': 'Gesellschaftsrecht',
      'trascrizioni e iscrizioni': 'Transkriptionen & Eintragungen',
      successioni: 'Erbschaft',
      'registrazioni e adempimenti': 'Registrierungen & Compliance',
      amministrazione: 'Verwaltung',
      'diritto immobiliare': 'Immobilienrecht'
    }
  },
  fr: {
    notaiTitle: 'Les Notaires',
    collaboratoriTitle: 'Notre Équipe',
    collaboratoriSubtitle: 'Notre équipe de professionnels',
    areas: {
      immobiliare: 'Immobilier',
      'diritto societario': 'Droit des Sociétés',
      'trascrizioni e iscrizioni': 'Transcriptions & Inscriptions',
      successioni: 'Successions',
      'registrazioni e adempimenti': 'Enregistrements & Conformité',
      amministrazione: 'Administration',
      'diritto immobiliare': 'Droit Immobilier'
    }
  }
};

const notai = [
  { name: 'Dott. Umberto Accordini', role: 'Notaio', initial: 'UA' },
  { name: 'Dott.ssa Carla Carillo', role: 'Notaio', initial: 'CC' }
];

const collaboratori = [
  { name: 'Nadia Viola', area: 'immobiliare', icon: Building2 },
  { name: 'Francesca Bissoli', area: 'immobiliare', icon: Building2 },
  { name: 'Angela Bianchera', area: 'diritto societario', icon: Briefcase },
  { name: 'Anna Guglielmi', area: 'trascrizioni e iscrizioni', icon: ClipboardList },
  { name: 'Antonella Annibaletti', area: 'successioni', icon: FileText },
  { name: 'Silvina Tabarelli', area: 'registrazioni e adempimenti', icon: BookOpen },
  { name: 'Arianna Renzi', area: 'amministrazione', icon: Scale },
  { name: 'Daniela Ravanini', area: 'diritto immobiliare', icon: Building2 },
  { name: 'Fabiana Dall\'Oca', area: 'diritto societario', icon: Briefcase }
];

const AboutPage = () => {
  const { t, language } = useLanguage();
  const tt = teamTranslations[language] || teamTranslations.it;

  return (
    <div data-testid="about-page" className="min-h-screen">
      <SEO page="about" />

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-[#121826]" />
        <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#C9A227]/40 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-[#C9A227] font-semibold mb-4">
              {t('about.subtitle')}
            </p>
            <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-6 max-w-3xl">
              {t('about.title')}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* About Content */}
      <section className="py-16 md:py-24 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <div className="relative">
                <div className="bg-[#283880] p-10 md:p-12 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-20 h-20 mx-auto mb-6 border border-[#C9A227]/50 flex items-center justify-center">
                      <Scale className="w-10 h-10 text-[#C9A227] stroke-[1]" />
                    </div>
                    <p className="font-['Playfair_Display'] text-xl text-white/85 italic leading-relaxed">
                      {t('ui.about.quote')}
                    </p>
                    <div className="h-[1px] w-16 mx-auto bg-[#C9A227]/40 mt-5 mb-3" />
                    <p className="text-[#C9A227] text-xs uppercase tracking-[0.2em] font-semibold">Francesco Carnelutti</p>
                  </div>
                </div>
                <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b border-r border-[#C9A227]/30" />
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#283880] tracking-tight mb-6">
                {t('about.subtitle')}
              </h2>
              <p className="text-[#5A6578] leading-relaxed mb-6 text-base md:text-lg">
                {t('about.description')}
              </p>
              <p className="text-[#5A6578] leading-relaxed text-base md:text-lg">
                {t('about.team')}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Notai */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-[#C9A227] font-semibold mb-3">{t('ui.home.heroEyebrow')}</p>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#283880] tracking-tight">
              {tt.notaiTitle}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-3xl mx-auto">
            {notai.map((notaio, index) => (
              <motion.div
                key={notaio.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                data-testid={`notaio-${index}`}
                className="text-center"
              >
                <div className="w-36 h-36 mx-auto mb-6 bg-[#283880] flex items-center justify-center relative">
                  <span className="font-['Playfair_Display'] text-4xl text-[#C9A227] font-bold">
                    {notaio.initial}
                  </span>
                  <div className="absolute -bottom-2 -right-2 w-8 h-8 border-b border-r border-[#C9A227]/40" />
                </div>
                <h3 className="font-['Playfair_Display'] text-xl font-semibold text-[#283880] mb-1">
                  {notaio.name}
                </h3>
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C9A227] font-semibold">
                  {t('ui.about.role')}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Collaboratori */}
      <section className="py-16 md:py-24 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-14">
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#283880] tracking-tight mb-3">
              {tt.collaboratoriTitle}
            </h2>
            <p className="text-[#5A6578] text-lg">{tt.collaboratoriSubtitle}</p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-[1px] bg-[rgba(30,36,51,0.08)]">
            {collaboratori.map((collab, index) => {
              const IconComponent = collab.icon;
              return (
                <motion.div
                  key={collab.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.04 }}
                  data-testid={`collaboratore-${index}`}
                  className="group bg-white p-6 hover:bg-[#283880] transition-all duration-400"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 bg-[#283880] group-hover:bg-white/10 border border-transparent group-hover:border-[#C9A227]/40 flex items-center justify-center flex-shrink-0 transition-all duration-400">
                      <User className="w-5 h-5 text-white group-hover:text-[#C9A227] transition-colors" />
                    </div>
                    <div>
                      <h3 className="font-['Playfair_Display'] text-lg font-semibold text-[#283880] group-hover:text-white mb-1 transition-colors">
                        {collab.name}
                      </h3>
                      <div className="flex items-center gap-2 text-[#C9A227]">
                        <IconComponent className="w-3.5 h-3.5 stroke-[1.5]" />
                        <span className="text-sm">{tt.areas[collab.area] || collab.area}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 md:py-24 bg-[#121826]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="h-[1px] w-full bg-gradient-to-r from-transparent via-[#C9A227]/30 to-transparent mb-16" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { it: 'Competenza', en: 'Expertise', de: 'Kompetenz', fr: 'Compétence' },
              { it: 'Affidabilità', en: 'Reliability', de: 'Zuverlässigkeit', fr: 'Fiabilité' },
              { it: 'Riservatezza', en: 'Confidentiality', de: 'Vertraulichkeit', fr: 'Confidentialité' }
            ].map((value, index) => (
              <motion.div
                key={value.it}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-8"
              >
                <div className="w-14 h-14 mx-auto mb-6 border border-[#C9A227]/40 flex items-center justify-center">
                  <span className="text-[#C9A227] font-['Playfair_Display'] text-xl font-bold">{index + 1}</span>
                </div>
                <h3 className="font-['Playfair_Display'] text-xl font-semibold text-white">
                  {value[language] || value.it}
                </h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
